import React, { useCallback, useState } from "react";
import ReactDOM from "react-dom";
import classNames from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUTurnUpRight,
  faFlag,
  faHourglassClock,
  faPen,
  faPersonGolfing,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { PutBookingParticipant } from "api/rpc/2024-04/facilityAdmin/teesheet/booking";
import { StatusCode } from "api/protocols";
import { Badge } from "components/badge/Badge";
import Popover from "components/popover/Popover";
import TimePick from "components/timePick/TimePick";
import { ButtonNew as Button } from "components/buttonNew";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { dequeue, enqueue, showError, showSuccess } from "redux/actions/ui";
import {
  determineOverPace,
  displayTurnTime,
  getGroupPaceTimes,
  isGroupOverPace,
} from "elements/teesheet/paceTracker/paceTrackerUtils";
import "elements/teesheet/paceTracker/paceTracker.scss";

export interface IPaceParticipantUpdate {
  id: number;
  tee_off_time: string;
  turn_time: string;
  finish_time: string;
}

export interface IPaceUpdateResult {
  teeOffTime: string;
  turnTime: string;
  finishTime: string;
  participants: IPaceParticipantUpdate[];
}

interface IPaceTrackerProps {
  teeOffTime: string;
  turnTime: string;
  finishTime: string;
  bookingParticipantIds: number[];
  onPaceUpdated: (result: IPaceUpdateResult) => void;
  onHide?: () => void;
  embedded?: boolean;
}

interface IEditTimeState {
  open: boolean;
  time: string;
}

export default function PaceTracker(props: IPaceTrackerProps) {
  const { teeOffTime, turnTime, finishTime, bookingParticipantIds, onPaceUpdated, onHide, embedded = false } = props;

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);

  const [editTeeOffTime, setEditTeeOffTime] = useState<IEditTimeState>({ open: false, time: "" });
  const [editTurnTime, setEditTurnTime] = useState<IEditTimeState>({ open: false, time: "" });
  const [editFinishTime, setEditFinishTime] = useState<IEditTimeState>({ open: false, time: "" });

  const facilityHoles = facilityStore?.facility?.holes ?? 18;
  const showTurnTime = displayTurnTime(facilityHoles);
  const eighteenHoleFacility = facilityHoles === 18;

  const paceTimes = { teeOffTime, turnTime, finishTime };
  const overPace = determineOverPace(paceTimes, facilityHoles, facilityStore?.teeSheetSettings);

  const handleGroupPace = useCallback(
    async (type: "tee_off_time" | "turn_time" | "finish_time", manualTime?: string) => {
      if (!bookingParticipantIds?.length) {
        return;
      }

      let updatedDateTimeStringParam: string | null = null;
      if (manualTime) {
        const localMoment = moment()?.startOf("day");
        const [hours, minutes] = manualTime.split(":").map(Number);
        localMoment?.set({ hour: hours, minute: minutes });
        const updatedMoment = localMoment?.clone()?.utc();

        if (type === "turn_time") {
          if (teeOffTime && updatedMoment.isBefore(moment.utc(teeOffTime, "YYYY-MM-DD, HH:mm"))) {
            dispatch(showError(t("Turn time can't be earlier than tee off time")));
            return;
          }
        }

        if (type === "finish_time") {
          if (turnTime && updatedMoment.isBefore(moment.utc(turnTime, "YYYY-MM-DD, HH:mm"))) {
            dispatch(showError(t("Finish time can't be earlier than turn time")));
            return;
          } else if (!turnTime && teeOffTime && updatedMoment.isBefore(moment.utc(teeOffTime, "YYYY-MM-DD, HH:mm"))) {
            dispatch(showError(t("Finish time can't be earlier than tee off time")));
            return;
          }
        }
        updatedDateTimeStringParam = updatedMoment.format("YYYY-MM-DD HH:mm");
      }

      ReactDOM.unstable_batchedUpdates(() => {
        setEditTeeOffTime({ open: false, time: "" });
        setEditTurnTime({ open: false, time: "" });
        setEditFinishTime({ open: false, time: "" });
      });

      dispatch(enqueue());
      const res = await Promise.all(
        bookingParticipantIds.map(id =>
          PutBookingParticipant({ id, [type]: updatedDateTimeStringParam ?? true }, false),
        ),
      );
      dispatch(dequeue());

      if (res?.some(data => data?.status !== StatusCode.OK)) {
        dispatch(showError(t("Error updating one or more participants")));
        return;
      }

      const foundSlot = res?.find(r => r?.status === StatusCode.OK && r?.data[type]);
      const foundParticipant = foundSlot ? foundSlot?.data : null;

      const participants: IPaceParticipantUpdate[] = res
        .filter(r => r?.status === StatusCode.OK)
        .map(r => ({
          id: r.data.id,
          tee_off_time: r.data.tee_off_time,
          turn_time: r.data.turn_time,
          finish_time: r.data.finish_time,
        }));

      onPaceUpdated({
        teeOffTime: foundParticipant ? foundParticipant.tee_off_time : teeOffTime,
        turnTime: foundParticipant ? foundParticipant.turn_time : turnTime,
        finishTime: foundParticipant ? foundParticipant.finish_time : finishTime,
        participants,
      });
      dispatch(showSuccess(t("Successfully recorded group pace time")));
    },
    [bookingParticipantIds, dispatch, onPaceUpdated, t, teeOffTime, turnTime, finishTime],
  );

  const renderStepLabel = (label: string, recorded: boolean, onEdit?: () => void) => {
    if (recorded && onEdit) {
      return (
        <div className="tee-times-group-pace-label-row" onClick={onEdit}>
          <p className="tee-times-group-pace-primary-text whitespace-nowrap">{label}</p>
          <FontAwesomeIcon className="tee-times-group-pace-edit-icon" icon={faPen} />
        </div>
      );
    }

    return <p className="tee-times-group-pace-primary-text whitespace-nowrap flex">{label}</p>;
  };

  const overPaceBadge = overPace ? (
    <Badge type="warning" size="small" outline>
      <FontAwesomeIcon className="pace-tracker-card-header-badge-icon mr-1" icon={faHourglassClock} /> {t("Over Pace")}
    </Badge>
  ) : null;

  return (
    <div className={classNames("pace-tracker-card", { "pace-tracker-card_embedded": embedded })}>
      <div className="pace-tracker-card-header">
        <div className="pace-tracker-card-header-start">
          <span className="pace-tracker-card-header-title">{t("Pace Tracker")}</span>
          {overPaceBadge}
        </div>
        {onHide ? (
          <Button type="link" size="small" onClick={onHide}>
            {t("Hide pace tracker")}
          </Button>
        ) : null}
      </div>

      <div className="tee-times-group-pace-button-container">
        <div className="tee-times-group-pace-step">
          <div
            className={classNames("tee-times-group-pace-button", {
              "tee-times-group-pace-button-recorded": teeOffTime,
            })}
            onClick={() =>
              teeOffTime
                ? setEditTeeOffTime(prev => ({
                    ...prev,
                    open: true,
                    time: moment.utc(teeOffTime, "YYYY-MM-DD HH:mm")?.local()?.format("HH:mm"),
                  }))
                : void handleGroupPace("tee_off_time")
            }
          >
            <FontAwesomeIcon icon={faPersonGolfing} />
          </div>
          <div className="tee-times-group-pace-button-text-group">
            {renderStepLabel(
              t("Tee Off"),
              Boolean(teeOffTime),
              teeOffTime
                ? () =>
                    setEditTeeOffTime(prev => ({
                      ...prev,
                      open: true,
                      time: moment.utc(teeOffTime, "YYYY-MM-DD HH:mm")?.local()?.format("HH:mm"),
                    }))
                : undefined,
            )}
            <p className="tee-times-group-pace-secondary-text text-center">
              {teeOffTime ? (
                <>
                  {t("Recorded at")} {moment.utc(teeOffTime, "YYYY-MM-DD HH:mm")?.local()?.format("LT")}
                </>
              ) : (
                t("Not recorded")
              )}
            </p>
          </div>
          <Popover
            overflow
            active={editTeeOffTime.open}
            handleClosePopover={() => setEditTeeOffTime({ open: false, time: "" })}
          >
            <div className="tee-time-edit-group-pace">
              <div className="tee-time-edit-group-pace-header">
                <p className="tee-time-edit-group-pace-header-primary">{t("Edit Tee Off Time")}</p>
                <FontAwesomeIcon
                  className="tee-time-edit-group-pace-header-icon"
                  icon={faXmark}
                  onClick={() => setEditTeeOffTime({ open: false, time: "" })}
                />
              </div>
              <div>
                <TimePick
                  value={editTeeOffTime?.time}
                  onChange={timeString => setEditTeeOffTime(prev => ({ ...prev, time: timeString }))}
                  size="medium"
                />
              </div>
              <div className="mt-4">
                <Button block type="quaternary" size="small" onClick={() => void handleGroupPace("tee_off_time")}>
                  {t("Save Current Time")}
                </Button>
              </div>
              <div className="tee-time-edit-group-pace-footer">
                <Button block type="secondary" size="medium" onClick={() => setEditTeeOffTime({ open: false, time: "" })}>
                  {t("Cancel")}
                </Button>
                <Button
                  block
                  type="primary"
                  size="medium"
                  onClick={() => void handleGroupPace("tee_off_time", editTeeOffTime?.time)}
                >
                  {t("Save")}
                </Button>
              </div>
            </div>
          </Popover>
        </div>

        {showTurnTime ? (
          <>
            <div
              className={classNames("tee-times-group-pace-divider", {
                "tee-times-group-pace-divider-recorded": turnTime,
              })}
            />
            <div className="tee-times-group-pace-step">
              <div
                className={classNames("tee-times-group-pace-button", {
                  "tee-times-group-pace-button-recorded": turnTime,
                  "tee-times-group-pace-button-disabled": !teeOffTime,
                })}
                onClick={
                  teeOffTime
                    ? () =>
                        turnTime
                          ? setEditTurnTime(prev => ({
                              ...prev,
                              open: true,
                              time: moment.utc(turnTime, "YYYY-MM-DD HH:mm")?.local()?.format("HH:mm"),
                            }))
                          : void handleGroupPace("turn_time")
                    : undefined
                }
              >
                <FontAwesomeIcon icon={faArrowUTurnUpRight} />
              </div>
              <div className="tee-times-group-pace-button-text-group">
                {renderStepLabel(
                  t("Turn"),
                  Boolean(turnTime),
                  turnTime
                    ? () =>
                        setEditTurnTime(prev => ({
                          ...prev,
                          open: true,
                          time: moment.utc(turnTime, "YYYY-MM-DD HH:mm")?.local()?.format("HH:mm"),
                        }))
                    : undefined,
                )}
                <p className="tee-times-group-pace-secondary-text text-center">
                  {turnTime ? (
                    <>
                      {t("Recorded at")} {moment.utc(turnTime, "YYYY-MM-DD HH:mm")?.local()?.format("LT")}
                    </>
                  ) : (
                    t("Not recorded")
                  )}
                </p>
              </div>
              <Popover
                overflow
                active={editTurnTime.open}
                handleClosePopover={() => setEditTurnTime({ open: false, time: "" })}
              >
                <div className="tee-time-edit-group-pace">
                  <div className="tee-time-edit-group-pace-header">
                    <p className="tee-time-edit-group-pace-header-primary">{t("Edit Turn Time")}</p>
                    <FontAwesomeIcon
                      className="tee-time-edit-group-pace-header-icon"
                      icon={faXmark}
                      onClick={() => setEditTurnTime({ open: false, time: "" })}
                    />
                  </div>
                  <div>
                    <TimePick
                      value={editTurnTime?.time}
                      onChange={timeString => setEditTurnTime(prev => ({ ...prev, time: timeString }))}
                      size="medium"
                    />
                  </div>
                  <div className="mt-4">
                    <Button block type="quaternary" size="small" onClick={() => void handleGroupPace("turn_time")}>
                      {t("Save Current Time")}
                    </Button>
                  </div>
                  <div className="tee-time-edit-group-pace-footer">
                    <Button
                      block
                      type="secondary"
                      size="medium"
                      onClick={() => setEditTurnTime({ open: false, time: "" })}
                    >
                      {t("Cancel")}
                    </Button>
                    <Button
                      block
                      type="primary"
                      size="medium"
                      onClick={() => void handleGroupPace("turn_time", editTurnTime?.time)}
                    >
                      {t("Save")}
                    </Button>
                  </div>
                </div>
              </Popover>
            </div>
          </>
        ) : null}

        <div
          className={classNames("tee-times-group-pace-divider", {
            "tee-times-group-pace-divider-recorded": finishTime,
          })}
        />
        <div className="tee-times-group-pace-step">
          <div
            className={classNames("tee-times-group-pace-button", {
              "tee-times-group-pace-button-recorded": finishTime,
              "tee-times-group-pace-button-disabled": (eighteenHoleFacility && !turnTime) || !teeOffTime,
            })}
            onClick={
              !((eighteenHoleFacility && !turnTime) || !teeOffTime)
                ? () =>
                    finishTime
                      ? setEditFinishTime(prev => ({
                          ...prev,
                          open: true,
                          time: moment.utc(finishTime, "YYYY-MM-DD HH:mm")?.local()?.format("HH:mm"),
                        }))
                      : void handleGroupPace("finish_time")
                : undefined
            }
          >
            <FontAwesomeIcon icon={faFlag} />
          </div>
          <div className="tee-times-group-pace-button-text-group">
            {renderStepLabel(t("Finish"), Boolean(finishTime))}
            <p className="tee-times-group-pace-secondary-text text-center">
              {finishTime ? (
                <>
                  {t("Recorded at")} {moment.utc(finishTime, "YYYY-MM-DD HH:mm")?.local()?.format("LT")}
                </>
              ) : (
                t("Not recorded")
              )}
            </p>
          </div>
          <Popover
            overflow
            active={editFinishTime.open}
            handleClosePopover={() => setEditFinishTime({ open: false, time: "" })}
            position="right"
          >
            <div className="tee-time-edit-group-pace">
              <div className="tee-time-edit-group-pace-header">
                <p className="tee-time-edit-group-pace-header-primary">{t("Edit Finish Time")}</p>
                <FontAwesomeIcon
                  className="tee-time-edit-group-pace-header-icon"
                  icon={faXmark}
                  onClick={() => setEditFinishTime({ open: false, time: "" })}
                />
              </div>
              <div>
                <TimePick
                  value={editFinishTime?.time}
                  onChange={timeString => setEditFinishTime(prev => ({ ...prev, time: timeString }))}
                  size="medium"
                />
              </div>
              <div className="mt-4">
                <Button block type="quaternary" size="small" onClick={() => void handleGroupPace("finish_time")}>
                  {t("Save Current Time")}
                </Button>
              </div>
              <div className="tee-time-edit-group-pace-footer">
                <Button block type="secondary" size="medium" onClick={() => setEditFinishTime({ open: false, time: "" })}>
                  {t("Cancel")}
                </Button>
                <Button
                  block
                  type="primary"
                  size="medium"
                  onClick={() => void handleGroupPace("finish_time", editFinishTime?.time)}
                >
                  {t("Save")}
                </Button>
              </div>
            </div>
          </Popover>
        </div>
      </div>
    </div>
  );
}

export { getGroupPaceTimes, isGroupOverPace };
